<template>
    <modal size="md" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title" v-if="job">Edit Job Information</h3>
            <h3 class="modal-title" v-else>Add Job Information</h3>
        </template>

        <slot>
            <div class="mb-2">
                <div class="p-1 unpad-x bg-150 rounded">
                    <div><span class="text-muted">Employee: </span>{{ employee.full_name }}</div>
                    <div><span class="text-muted">Company: </span>{{ employee.company.name }}</div>
                    <div v-if="job"><span class="text-muted">Position: </span>{{ job.position.terminal_value }}</div>
                </div>
            </div>

            <div class="form-row" v-if="!job">
                <div class="col">
                    <form-input label="Position" v-model="formData.position" :errors="errors.position" type="select" :options="positionTypeOptions" :noblank="true" @update:model-value="onPositionChange"/>
                </div>
            </div>

            <div class="form-row">
                <div class="col">
                    <form-input label="Pay Type" v-model="formData.pay_type" :errors="errors.pay_type" type="select" :options="payTypeOptions" :noblank="true" />
                </div>
            </div>

            <div class="form-row">
                <div class="col col-6" v-if="formData.pay_type == 'H'">
                    <form-input label="Hourly Rate" helper-text="per pay hour" v-model="formData.hourly_rate" :errors="errors.hourly_rate" type="currency" />
                </div>

                <div class="col col-6"  v-if="formData.pay_type == 'H' && selectedPosition && selectedPosition.is_regularly_tipped && client.uses_explicit_tip_credit">
                    <form-input label="Tip Credit" v-model="formData.tip_credit_rate" :errors="errors.tip_credit_rate" type="currency" />
                </div>

                <div class="col col-6" v-if="['S', 'SNE'].includes(formData.pay_type)">
                    <form-input label="Salary Rate" helper-text="per pay period" v-model="formData.salary_rate" :errors="errors.salary_rate" type="currency" />
                </div>

                <div class="col col-6" v-if="['S', 'SNE'].includes(formData.pay_type)">
                    <form-input label="Default Hours" v-model="formData.default_hours" :errors="errors.default_hours" type="integer" />
                </div>

                <div class="col col-6" v-if="formData.pay_type == 'P'">
                    <form-input label="Rate" helper-text="per unit" v-model="formData.unit_rate" :errors="errors.unit_rate" type="currency" />
                </div>

            </div>

            <h5 class="table-section-title pt-2">Additional Compensation</h5>
            <hr class="mt-1 mb-2"/>

            <template v-if="formData.additional_compensation.length">
                <div class="form-row">
                    <div class="col col-7">
                        <h5 class="text-muted">Earning</h5>
                    </div>

                    <div class="col col-4">
                        <h5 class="text-muted">Rate</h5>
                    </div>

                    <div class="col col-1">
                    </div>
                </div>

                <hr class="mt-1 mb-1"/>

                <div class="form-row" v-for="ac,idx in formData.additional_compensation">
                    <div class="col col-7">
                        <form-input
                            label=""
                            v-model="ac.earning_type_slug"
                            :errors="errors.additional_compensation[idx].earning_type_slug"
                            type="select"
                            :options="earningTypeOptions"
                            :noblank="true"
                        />
                    </div>

                    <div class="col col-4 pt-2">
                        <!-- Base-rate earnings compute their rate from the hourly rate; show it instead of an input. -->
                        <form-input
                            v-if="['per-unit', 'hourly'].includes(selectedEarningTypes[idx]?.type) && !selectedEarningTypes[idx]?.is_tied_to_base_rate"
                            :helper-text="selectedEarningTypes[idx].type_pretty.toLowerCase()"
                            label=""
                            v-model="ac.rate"
                            :errors="errors.additional_compensation[idx].rate"
                            type="currency"
                        />
                        <div v-else-if="selectedEarningTypes[idx]?.is_tied_to_base_rate" class="text-muted">
                            <span v-if="calculatedBaseRate(idx) !== null">${{ calculatedBaseRate(idx) }}</span>
                            <span v-else>&mdash;</span>
                            <span class="text-small d-block">({{ baseRateLabel(idx) }})</span>
                        </div>
                    </div>

                    <div class="col col-1 pt-2">
                        <button class="btn btn-link" @click.prevent="removeAdditionalCompensation(idx)"><i class="far fa-trash-alt"></i></button>
                    </div>
                </div>
            </template>

            <div class="form-row mt-2 mb-2" v-else-if="!positionDefaultEarningTypes.length">
                <div class="col text-center">
                    <i>This employee does not have any additional earnings.</i>
                </div>
            </div>

            <!-- The position's defaults: added on the paygrid for every job at the position -->
            <template v-if="positionDefaultEarningTypes.length">
                <div class="text-small text-muted mt-2">From the position (added on the paygrid automatically)</div>
                <div class="form-row text-muted" v-for="cet in positionDefaultEarningTypes" :key="cet.id">
                    <div class="col col-7 pt-1">{{ cet.name }}</div>
                    <div class="col col-4 pt-1">
                        <template v-if="cet.is_tied_to_base_rate">
                            <span v-if="baseRateFor(cet) !== null">${{ baseRateFor(cet) }}</span>
                            <span v-else>&mdash;</span>
                            <span class="text-small d-block">({{ baseRateLabelFor(cet) }})</span>
                        </template>
                        <span v-else class="text-small">{{ cet.type_pretty }}, entered on the paygrid</span>
                    </div>
                </div>
            </template>

            <button-row class="pt-0">
                <button
                    class="btn btn-link"
                    :class="{'disabled': earningTypeOptions.length <= formData.additional_compensation.length}"
                    @click.prevent="addAdditionalCompensation()">
                    <i class="far fa-plus"></i> Add Earning
                </button>
            </button-row>

            <div class="form-row">
                <div class="col">
                    <form-input
                        label="Effective Pay Period"
                        v-model="effectiveDateSelection"
                        type="select"
                        :options="effectiveDateOptions"
                        :noblank="true"
                        :errors="effectiveDateSelection === 'custom' ? [] : errors.effective_on"
                    />
                </div>
            </div>
            <div class="form-row" v-if="effectiveDateSelection === 'custom'">
                <div class="col col-6">
                    <form-input label="Effective Date" v-model="formData.effective_on" :errors="errors.effective_on" type="date" />
                </div>
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>

                <button type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import moment from 'moment-timezone'

export default {
    props: ['client', 'employee', 'job', 'futureMutations', 'clientEarningTypes', 'positions', ],
    mixins: [FormMixin, ModalMixin],
    components: {},
    emits: ['updated', 'close', ],
    data() {
        const dateValidation = {dateOnly: true}

        if (this.job) {
            if (this.job.effective_on) {
                dateValidation.earliest = moment(this.job.effective_on).format('YYYY-MM-DD')
            }
            else {
                dateValidation.earliest = moment().tz('America/New_York').format('YYYY-MM-DD')
            }
        }

        const data = {
            formData: this.makeFormData(),
            formRules: {
                pay_type: {presence: {allowEmpty: false}, },
                effective_on: {presence: {allowEmpty: false}, date: dateValidation},
            },
            formLabels: {
            },
        }

        data.errors = {
            additional_compensation: []
        }

        // effectiveDateSelection: a pay period start date, or 'custom' for the free-form date input.
        data.payPeriodOptions = []
        data.effectiveDateSelection = 'custom'

        if (this.job) {
            data.formRules.position = {presence: {allowEmpty: false}}

            this.job.additional_compensation.forEach(ac => {
                data.errors.additional_compensation.push({})
            })
        }

        return data
    },
    watch: {
        'formData.pay_type'() {
            if (this.formData.pay_type == 'H') {
                this.formData.default_hours = ''
            }
            else {
                this.formData.tip_credit_rate = ''
            }
        },
        effectiveDateSelection(value) {
            // Picking a period sets effective_on to its start date; 'custom' leaves it editable.
            if (value !== 'custom') {
                this.formData.effective_on = moment(value).format('MM/DD/YYYY')
            }
        },
    },
    mounted() {
        this.fetchPayPeriodOptions()
    },
    computed: {
        company() {
            return this.client.companies.find(c => c.id == this.employee.company_id)
        },
        selectedPosition() {
            let positionId = null

            if (this.job?.position) {
                if (this.job.position.id) {
                    return this.job.position
                } else {
                    positionId = this.job.position_id
                }
            } else if (this.formData.position) {
                positionId = this.formData.position
            }

            if (positionId) {
                return this.positions.find(p => p.id == positionId)
            }
            return null
        },
        positionTypeOptions() {
            const result = []

            const existingsPositionIds = this.employee.jobs.map(j => j.is_active && j.position_id)
            existingsPositionIds.push(...this.futureMutations.map(m => !m.is_applied && m.position_id))

            this.positions.forEach(p => {
                if (p.company_id == this.employee.company_id) {
                    if (!existingsPositionIds.includes(p.id)) {
                        result.push({text: p.terminal_value, value: p.id})
                    }
                }
            })

            result.sort((a, b) => a.text > b.text ? 1 : -1)

            return result
        },
        payTypeOptions() {
            return [
                {text: 'Hourly', value: 'H'},
                {text: 'Salary', value: 'S'},
                {text: 'Piecemeal', value: 'P'},
            ]
        },
        earningTypeOptions() {
            const result = []
            this.clientEarningTypes.filter(cet => cet.company_id == this.employee.company_id).forEach(cet => {
                if (['regular', 'overtime', 'tips'].includes(cet.earning_type.slug)) {return}
                result.push({text: cet.name, value: cet.slug})
            })

            result.sort((a, b) => a.text > b.text ? 1 : -1)

            return result
        },
        positionDefaultEarningTypes() {
            // Read-only: the paygrid adds these for every job at the position, after the
            // job's own additional compensation; ones the job already lists aren't repeated
            const defaultIds = this.selectedPosition?.default_company_earning_type_ids || []
            const listedSlugs = this.formData.additional_compensation.map(ac => ac.earning_type_slug)
            return this.clientEarningTypes
                .filter(cet => defaultIds.includes(cet.id))
                .filter(cet => !['regular', 'overtime'].includes(cet.earning_type.slug))
                .filter(cet => !listedSlugs.includes(cet.slug))
                .sort((a, b) => a.name > b.name ? 1 : -1)
        },
        selectedEarningTypes() {
            const result = []
            this.formData.additional_compensation.forEach(ac => {
                const cet = this.clientEarningTypes.find(cet => cet.slug == ac.earning_type_slug)
                result.push(cet ? cet : {})
            })

            return result
        },
        currentPayPeriod() {
            const today = moment().tz('America/New_York').format('YYYY-MM-DD')
            return this.payPeriodOptions.find(pp =>
                pp.pay_period_start_date <= today && pp.pay_period_end_date >= today
            ) || null
        },
        effectiveDateOptions() {
            const options = this.payPeriodOptions.map(pp => ({
                text: this.payPeriodLabel(pp),
                value: pp.pay_period_start_date,
            }))
            options.push({text: 'Custom', value: 'custom'})
            return options
        },
    },
    methods: {
        makeFormData() {
            const data = {
                position: this.job ? this.job.position_id : '',
                pay_type: this.job ? this.job.pay_type : 'H',
                hourly_rate: this.job ? this.job.hourly_rate : '',
                tip_credit_rate: this.job ? this.job.tip_credit_rate : '',
                salary_rate: this.job ? this.job.salary_rate : '',
                unit_rate: this.job ? this.job.unit_rate : '',
                default_hours: this.job ? this.job.default_hours : '',
                additional_compensation: this.job ? JSON.parse(JSON.stringify(this.job.additional_compensation)) : [],
                // Stored as MM/DD/YYYY to match the date input's format and the form's date validator.
                effective_on: this.job?.effective_on
                    ? moment(this.job.effective_on, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('MM/DD/YYYY')
                    : moment().format('MM/DD/YYYY'),
            }

            return data
        },
        getFormRules() {
            const rules = JSON.parse(JSON.stringify(this.formRules))

            if (!this.job) {
                rules.position = {presence: {allowEmpty: false}, }
            }

            // Period options can start in the past, so the not-before-today rule only
            // applies to custom dates.
            if (this.effectiveDateSelection !== 'custom') {
                rules.effective_on = {presence: {allowEmpty: false}, date: {dateOnly: true}}
            }

            if (['S', 'SNE'].includes(this.formData.pay_type)) {
                rules.default_hours = {presence: {allowEmpty: false}, }
                rules.salary_rate = {presence: {allowEmpty: false}, }
            }

            if (['H'].includes(this.formData.pay_type)) {
                rules.hourly_rate = {presence: {allowEmpty: false}, }
            }

            if (['P'].includes(this.formData.pay_type)) {
                rules.unit_rate = {presence: {allowEmpty: false}, }
            }

            return rules
        },
        validate() {
            let isValid = FormMixin.methods.validate.call(this)

            this.errors.additional_compensation = []
            this.formData.additional_compensation.forEach((ac, idx) => {
                this.errors.additional_compensation[idx] = {}

                if (!ac.earning_type_slug) {
                    isValid = false
                    this.errors.additional_compensation[idx].earning_type_slug = ["Earning type can't be blank."]
                }
                else if (['per-unit', 'hourly'].includes(this.selectedEarningTypes[idx]?.type) && !this.selectedEarningTypes[idx]?.is_tied_to_base_rate) {
                    if (!ac.rate) {
                        isValid = false
                        this.errors.additional_compensation[idx].rate = ["Rate can't be blank."]
                    }
                }
            })

            return isValid
        },
        addAdditionalCompensation() {
            this.formData.additional_compensation.push({
                earning_type_slug: '',
            })

            if (!this.errors.additional_compensation) {
                this.errors.additional_compensation = []
            }
            this.errors.additional_compensation.push({})
        },
        removeAdditionalCompensation(idx) {
            this.formData.additional_compensation.splice(idx, 1)
            this.errors.additional_compensation.splice(idx, 1)
        },
        calculatedBaseRate(idx) {
            return this.baseRateFor(this.selectedEarningTypes[idx])
        },
        baseRateLabel(idx) {
            return this.baseRateLabelFor(this.selectedEarningTypes[idx])
        },
        baseRateFor(cet) {
            // hourly * multiplier + addition, matching the backend seed; null when there
            // is no hourly rate to compute from.
            if (!cet || !cet.is_tied_to_base_rate) {
                return null
            }
            const hourlyRate = parseFloat(this.formData.hourly_rate)
            if (isNaN(hourlyRate)) {
                return null
            }
            const multiplier = parseFloat(cet.base_rate_multiplier) || 0
            const addition = parseFloat(cet.base_rate_addition) || 0
            return (hourlyRate * multiplier + addition).toFixed(2)
        },
        baseRateLabelFor(cet) {
            // e.g. "1.5x Hourly Rate + $2.00"
            if (!cet || !cet.is_tied_to_base_rate) {
                return ''
            }
            const multiplier = parseFloat(cet.base_rate_multiplier) || 0
            const addition = parseFloat(cet.base_rate_addition) || 0
            let label = `${multiplier}x Hourly Rate`
            if (addition) {
                label += ` + $${addition.toFixed(2)}`
            }
            return label
        },
        onPositionChange() {
            // The position's default earnings aren't copied into the job's additional
            // compensation: the paygrid adds them for every job at the position on its own.
            // The paygroup (and thus its pay periods) is determined by the selected position.
            this.fetchPayPeriodOptions()
        },
        fetchPayPeriodOptions() {
            const paygroupId = this.selectedPosition?.paygroup_id
            if (!paygroupId) {
                this.payPeriodOptions = []
                this.effectiveDateSelection = 'custom'
                return
            }
            return this.$api.get(`/clients/${this.client.id}/paygroups/${paygroupId}/pay-period-options`).then(resp => {
                this.payPeriodOptions = resp
                this.initEffectiveDateSelection()
            }).catch(() => {
                this.payPeriodOptions = []
                this.effectiveDateSelection = 'custom'
            })
        },
        initEffectiveDateSelection() {
            // Preselect the period matching the saved effective date, else the current
            // period, else 'custom'. The watcher then keeps effective_on in step.
            const currentIso = moment(this.formData.effective_on, ['MM/DD/YYYY', 'YYYY-MM-DD']).format('YYYY-MM-DD')
            const match = this.payPeriodOptions.find(pp => pp.pay_period_start_date === currentIso)
            if (match) {
                this.effectiveDateSelection = match.pay_period_start_date
            }
            else if (this.currentPayPeriod) {
                this.effectiveDateSelection = this.currentPayPeriod.pay_period_start_date
            }
            else {
                this.effectiveDateSelection = 'custom'
            }
        },
        payPeriodLabel(pp) {
            return `${this.$filters.date(pp.pay_period_start_date)} - ${this.$filters.date(pp.pay_period_end_date)}`
        },
        getFormData() {
            const data = JSON.parse(JSON.stringify(this.formData))
            if (!this.client.uses_explicit_tip_credit) {
                delete data.tip_credit_rate
            }

            if (['S', 'SNE'].includes(data.pay_type)) {
                console.log(1)
                delete data.hourly_rate
                delete data.unit_rate
            }

            if (['H'].includes(data.pay_type)) {
                console.log(2)
                delete data.default_hours
                delete data.salary_rate
                delete data.unit_rate
            }

            if (['P'].includes(data.pay_type)) {
                console.log(3)
                delete data.default_hours
                delete data.salary_rate
                delete data.hourly_rate
            }

            return data
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            const data = this.getFormData()

            this.$store.dispatch('START_LOADING')
            return this.$api.post(`/clients/${this.client.id}/employees/${this.employee.id}/job-mutations`, data).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully saved job information.`)
                this.$emit('updated', resp)
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.errors = errors

                if (!this.errors.additional_compensation) {
                    this.errors.additional_compensation = []
                }

                for (let idx = 0; idx < this.formData.additional_compensation.length; idx++) {
                    this.errors.additional_compensation[idx] = this.errors.additional_compensation[idx] || {}
                }

                this.formErrors = (errors.__all__ || [])
                this.$bus.showError(errors.__all__)
            })
        }
    }
}
</script>
